import { z } from 'zod';
import { emailSchema, tenantCodeSchema } from './common';
import {
  exposureModeSchema,
  gatewayRouteSchema,
  shortNameSchema,
  tenantRecordSchema,
} from './central';
import { tenantStatusSchema } from './directory';
import { canonicalIpv6, isTailnetAddress } from './network';
import { releaseVersionSchema, tenantVersionsSchema, versionStatusSchema } from './school-version';
import { updateNoticeSchema } from './updater';

/**
 * Host Admin API DTOs (central-api `/v1/admin/*`, used by central-web). Routes and auth are
 * listed in CENTRAL_ROUTES; these are the request/response bodies.
 */

export const hostnameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'Invalid hostname');

/**
 * The school's tailnet IP: 100.64.0.0/10 or fd7a:115c:a1e0::/48 only (no hostnames, no other
 * IPs), because central sends signed requests and gateway traffic to it. Rendered into the
 * gateway config, so kept strict. Unique across tenants (central-api enforces it), so IPv6 is
 * stored in its one RFC 5952 spelling (`fd7a:115c:a1e0:0::1` → `fd7a:115c:a1e0::1`, review N7).
 */
export const tailscaleAddressSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.union([z.ipv4(), z.ipv6()]))
  .transform((ip) => (ip.includes(':') ? (canonicalIpv6(ip) ?? ip) : ip))
  .refine((ip) => !ip.startsWith('::ffff:') && isTailnetAddress(ip), {
    message: 'Must be a Tailscale IP (100.64.0.0/10 or fd7a:115c:a1e0::/48)',
  })
  .describe('Tailscale IP: 100.64.0.0/10 or fd7a:115c:a1e0::/48, unique across tenants');

const nullableText = (max: number) => z.string().trim().max(max).nullable();

const subscriptionInputSchema = z.object({
  plan: nullableText(60).default(null),
  startsOn: z.iso.date().nullable().default(null),
  endsOn: z.iso.date().nullable().default(null),
  contactName: nullableText(120).default(null),
  contactEmail: emailSchema.nullable().default(null),
});

/**
 * PATCH: only the fields sent change. No defaults here: `.partial()` of the create schema kept
 * them, so a patch of one field reset the others to null (found 2026-10-10).
 */
const subscriptionPatchSchema = z.object({
  plan: nullableText(60).optional(),
  startsOn: z.iso.date().nullable().optional(),
  endsOn: z.iso.date().nullable().optional(),
  contactName: nullableText(120).optional(),
  contactEmail: emailSchema.nullable().optional(),
});

/** Subscription dates must be in order (ISO dates compare as strings). Null means open. */
export function subscriptionDatesProblem(
  startsOn: string | null | undefined,
  endsOn: string | null | undefined,
): string | null {
  return startsOn && endsOn && endsOn < startsOn ? 'Ends on must not be before Starts on' : null;
}

/**
 * Fields that are read-only once the school has enrolled (owner decision 2026-10-10): the
 * custom domain is the school's sign-in address (issuer), exposure changes how traffic reaches
 * it, and the Tailscale address can be set once (until install sets it) but not changed.
 */
export const LOCKED_AFTER_ENROLLMENT = [
  'customDomain',
  'exposureMode',
  'tailscaleAddress',
] as const;
export type LockedAfterEnrollment = (typeof LOCKED_AFTER_ENROLLMENT)[number];

/**
 * First Tenant Admin, entered by the Host Admin and handed to the school once, in the enrollment
 * response. Central deletes it when the school enrolls (SECURITY.md #1: no school data).
 */
export const bootstrapAdminEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(emailSchema)
  .describe('First Tenant Admin email; deleted from central when the school enrolls');

export const createTenantSchema = z.object({
  code: tenantCodeSchema,
  name: z.string().trim().min(1).max(120),
  /** Required on create (2026-10-10): app header, home screen and the welcome email. */
  shortName: shortNameSchema,
  bootstrapAdminEmail: bootstrapAdminEmailSchema,
  customDomain: hostnameSchema.nullable().default(null),
  exposureMode: exposureModeSchema.default('passthrough'),
  tailscaleAddress: tailscaleAddressSchema.nullable().default(null),
  status: tenantStatusSchema.default('active'),
  subscription: subscriptionInputSchema
    .default({
      plan: null,
      startsOn: null,
      endsOn: null,
      contactName: null,
      contactEmail: null,
    })
    .superRefine((sub, ctx) => {
      const problem = subscriptionDatesProblem(sub.startsOn, sub.endsOn);
      if (problem) ctx.addIssue({ code: 'custom', message: problem, path: ['endsOn'] });
    }),
});
export type CreateTenant = z.infer<typeof createTenantSchema>;
export type CreateTenantInput = z.input<typeof createTenantSchema>;

export const updateTenantSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  /** Central's record only; a school that has enrolled keeps its own (Admin → Branding). */
  shortName: shortNameSchema.optional(),
  customDomain: hostnameSchema.nullable().optional(),
  exposureMode: exposureModeSchema.optional(),
  tailscaleAddress: tailscaleAddressSchema.nullable().optional(),
  status: tenantStatusSchema.optional(),
  subscription: subscriptionPatchSchema.optional(),
  /** Set or replace the first Tenant Admin for the next (re-)enrollment; cleared when it runs. */
  bootstrapAdminEmail: bootstrapAdminEmailSchema.optional(),
  /** Required when the change moves the canonical hostname (issuer + OAuth redirects change). */
  confirmCanonicalChange: z.boolean().optional(),
});
export type UpdateTenant = z.infer<typeof updateTenantSchema>;

export const tenantHealthSchema = z.object({
  checkedAt: z.iso.datetime(),
  ok: z.boolean(),
  version: z.string().nullable(),
  uptimeSeconds: z.number().int().nullable(),
  database: z.string().nullable(),
  keycloak: z.string().nullable(),
  userCount: z.number().int().nullable(),
  error: z.string().nullable(),
});
export type TenantHealth = z.infer<typeof tenantHealthSchema>;

export const enrollmentTokenStateSchema = z.enum(['active', 'used', 'revoked', 'expired']);
export const enrollmentTokenViewSchema = z.object({
  id: z.uuid(),
  createdBy: z.string(),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  usedAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable(),
  state: enrollmentTokenStateSchema,
});
export type EnrollmentTokenView = z.infer<typeof enrollmentTokenViewSchema>;
/** POST …/enrollment-token: the plaintext token is returned exactly once. */
export const issuedEnrollmentTokenSchema = enrollmentTokenViewSchema.extend({ token: z.string() });
export type IssuedEnrollmentToken = z.infer<typeof issuedEnrollmentTokenSchema>;

/**
 * Welcome email to the first Tenant Admin (owner decision 2026-10-10 #3): sent from central's
 * SMTP when the school is created, resendable until it enrolls. `error` never holds the address.
 */
export const welcomeEmailStateSchema = z.enum(['sent', 'failed']);
export const welcomeEmailStatusSchema = z.object({
  state: welcomeEmailStateSchema,
  at: z.iso.datetime(),
  error: z.string().max(300).nullable(),
});
export type WelcomeEmailStatus = z.infer<typeof welcomeEmailStatusSchema>;

export const tenantViewSchema = tenantRecordSchema.extend({
  canonicalHostname: z.string(),
  hostnames: z.array(z.string()),
  credentialsIssuedAt: z.iso.datetime().nullable(),
  /** Additive: first Tenant Admin waiting for enrollment; null once the school has enrolled. */
  bootstrapAdminEmail: emailSchema.nullable().default(null),
  health: tenantHealthSchema.nullable(),
  /** Latest enrollment token (any state), or null if none was ever issued. */
  enrollmentToken: enrollmentTokenViewSchema.nullable(),
  /** Additive: last versions the school reported, null until its first successful poll. */
  versions: tenantVersionsSchema.nullable().default(null),
  /**
   * Additive: the release central compares against: the newer of TP_LATEST_RELEASE and the
   * updater's latest checked release (LIVE L4), null if neither is known.
   */
  latestRelease: releaseVersionSchema.nullable().default(null),
  /** Additive: running version (release, else tenant-api) against latestRelease. */
  versionStatus: versionStatusSchema.default('unknown'),
  /** Additive (2026-10-10): last welcome email attempt; null when none was made yet. */
  welcomeEmail: welcomeEmailStatusSchema.nullable().default(null),
  /** Additive (LIVE L4): the update email for latestRelease while the school is behind it. */
  updateNotice: updateNoticeSchema.nullable().default(null),
});
export type TenantView = z.infer<typeof tenantViewSchema>;

export const gatewaySyncSchema = z.object({
  at: z.iso.datetime(),
  ok: z.boolean(),
  changed: z.boolean(),
  routeCount: z.number().int(),
  error: z.string().nullable(),
});
export type GatewaySync = z.infer<typeof gatewaySyncSchema>;

export const gatewayStatusSchema = z.object({
  routes: z.array(gatewayRouteSchema),
  lastSync: gatewaySyncSchema.nullable(),
});
export type GatewayStatus = z.infer<typeof gatewayStatusSchema>;

/** GET /v1/admin/tenants */
export const tenantListSchema = z.object({
  tenants: z.array(tenantViewSchema),
  gateway: gatewayStatusSchema,
  /** Additive: latest released version (TP_LATEST_RELEASE), null if unset. */
  latestRelease: releaseVersionSchema.nullable().default(null),
});
export type TenantList = z.infer<typeof tenantListSchema>;

export const canonicalWarningSchema = z.object({
  code: z.literal('CANONICAL_HOSTNAME_CHANGED'),
  from: z.string(),
  to: z.string(),
  message: z.string(),
});

/** Create succeeded but the welcome email did not go out (SMTP not configured or refused). */
export const welcomeEmailWarningSchema = z.object({
  code: z.literal('WELCOME_EMAIL_NOT_SENT'),
  message: z.string(),
});

export const tenantWarningSchema = z.discriminatedUnion('code', [
  canonicalWarningSchema,
  welcomeEmailWarningSchema,
]);
export type TenantWarning = z.infer<typeof tenantWarningSchema>;

/** Result of telling the school its new status (PUT /private/v1/tenant-status). */
export const statusSyncSchema = z.object({ ok: z.boolean(), error: z.string().nullable() });

/** POST / PATCH /v1/admin/tenants[/:code] */
export const tenantMutationSchema = z.object({
  tenant: tenantViewSchema,
  warnings: z.array(tenantWarningSchema),
  gateway: gatewaySyncSchema,
  statusSync: statusSyncSchema.nullable(),
});
export type TenantMutation = z.infer<typeof tenantMutationSchema>;

export const certificateStatusSchema = z.object({
  hostname: z.string(),
  ok: z.boolean(),
  subject: z.string().nullable(),
  issuer: z.string().nullable(),
  validTo: z.iso.datetime().nullable(),
  error: z.string().nullable(),
});
export type CertificateStatus = z.infer<typeof certificateStatusSchema>;

/** GET /v1/admin/tenants/:code/health[?refresh=true] */
export const tenantHealthQuerySchema = z.object({
  refresh: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
});
export const tenantHealthReportSchema = z.object({
  health: tenantHealthSchema.nullable(),
  certificates: z.array(certificateStatusSchema),
});
export type TenantHealthReport = z.infer<typeof tenantHealthReportSchema>;

export const hostAuditEntrySchema = z.object({
  id: z.number().int(),
  at: z.iso.datetime(),
  actor: z.string(),
  action: z.string(),
  tenantCode: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
  ip: z.string().nullable(),
});
export type HostAuditEntry = z.infer<typeof hostAuditEntrySchema>;
export const hostAuditQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  before: z.coerce.number().int().min(1).optional(),
  tenantCode: tenantCodeSchema.optional(),
});
export const hostAuditPageSchema = z.object({
  entries: z.array(hostAuditEntrySchema),
  nextBefore: z.number().int().nullable(),
});
export type HostAuditPage = z.infer<typeof hostAuditPageSchema>;

export const pushStatsRowSchema = z.object({
  tenantCode: z.string(),
  sent: z.number().int(),
  failed: z.number().int(),
  invalid: z.number().int(),
  rateLimited: z.number().int(),
});
export type PushStatsRow = z.infer<typeof pushStatsRowSchema>;
export const pushStatsQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90).default(7),
});
export const pushStatsSchema = z.object({ since: z.iso.date(), rows: z.array(pushStatsRowSchema) });
export type PushStats = z.infer<typeof pushStatsSchema>;

/** PUT /v1/admin/tenants/:code/ops/* */
export const opsResultSchema = z.object({ ok: z.boolean(), tenantStatusCode: z.number().int() });
export type OpsResult = z.infer<typeof opsResultSchema>;

/** POST /v1/admin/tenants/:code/welcome-email: resend, only while the school has not enrolled. */
export const welcomeEmailResultSchema = z.object({ welcomeEmail: welcomeEmailStatusSchema });
export type WelcomeEmailResult = z.infer<typeof welcomeEmailResultSchema>;

export const tenantCodeParamsSchema = z.object({ code: tenantCodeSchema });
